/**
 * Shared types + presentation metadata for the party Rates & Charges grids.
 *
 * The grids render these display-row shapes; live data from `useCustomerRateCard`
 * is mapped into them by `liveMappings.ts`. The rate-method / rule-type / fuel-
 * territory metadata below drives the Add forms.
 */
import type { FuelScopeKindValue, LaneMatchModeValue } from '@my/tms-api';

// ── Transport ────────────────────────────────────────────────────────────────

/** Which kind of charge the operator is adding. Drives the form shape. */
export type TransportRuleType = 'rateSheet' | 'routeSheet';

/** How a rate sheet turns quantity into a charge (matches the wire enum). */
export type RateMethod = 'Unit' | 'Band' | 'RangeAccum' | 'RangeSplit';

export interface RuleTypeMeta {
  value: TransportRuleType;
  label: string;
  hint: string;
}

export const RULE_TYPES: readonly RuleTypeMeta[] = [
  { value: 'rateSheet', label: 'Rate sheet', hint: 'Price by cargo type & quantity' },
  { value: 'routeSheet', label: 'Route sheet', hint: 'Fixed prices per lane' },
] as const;

export interface RateMethodMeta {
  value: RateMethod;
  /** Human-friendly title for the card / grid. */
  label: string;
  hint: string;
}

export const RATE_METHODS: readonly RateMethodMeta[] = [
  {
    value: 'Unit',
    label: 'Unit',
    hint: 'Each ordinal unit has its own rate — 1st unit €X, 2nd €Y…',
  },
  {
    value: 'Band',
    label: 'Band',
    hint: 'Total quantity determines band — band rate applies to all units.',
  },
  {
    value: 'RangeAccum',
    label: 'Range accumulate',
    hint: 'Each range up to total incurs its own flat charge — all summed.',
  },
  {
    value: 'RangeSplit',
    label: 'Range split',
    hint: "Bracket-style — each portion charged at that range's rate.",
  },
] as const;

/** Friendly label for a rate method value (falls back to the raw value). */
export function rateMethodLabel(v: RateMethod): string {
  return RATE_METHODS.find((m) => m.value === v)?.label ?? v;
}

/** Period status for any time-versioned rate (transport sheets, fuel surcharge). */
export type PeriodStatus = 'Planned' | 'Current' | 'Prior';
/** @deprecated alias — prefer {@link PeriodStatus}. */
export type FuelStatus = PeriodStatus;

/** Status → dot colour. Planned = amber, Current = emerald, Prior = rose. */
export const PERIOD_STATUS_DOT: Record<PeriodStatus, string> = {
  Planned: 'bg-amber-500',
  Current: 'bg-emerald-500',
  Prior: 'bg-rose-500',
};
/** @deprecated alias — prefer {@link PERIOD_STATUS_DOT}. */
export const FUEL_STATUS_DOT = PERIOD_STATUS_DOT;

// ── Display-row shapes (what the grids render) ──────────────────────────────

/** A row in the Transport "Rate engine rules" sub-table. */
export interface RateEngineRule {
  id: string;
  /** Charged on its own when a job matches, or offered first. */
  autoApply: boolean;
  /** Headline cargo label, e.g. "Pallets". */
  cargoLabel: string;
  /** Applies-to chips (item / UOM labels). */
  appliesTo: string[];
  /** Friendly rate-method label, e.g. "Band". */
  methodLabel: string;
  /** Pre-formatted rate, e.g. "€5.00 / unit" or "Banded". */
  rateLabel: string;
  /** Pre-formatted minimum charge, e.g. "€15.00", or "—". */
  minCharge: string;
  fromLabel: string;
  toLabel: string;
  status: PeriodStatus;
}

/**
 * How strictly a lane has to match a job for its price to apply. Exact is the
 * direct run; Contains is the same endpoints with drops along the way. Both can
 * exist for one lane + cargo type — Exact is the more specific match.
 */
export const LANE_MATCH_MODES: {
  value: LaneMatchModeValue;
  label: string;
  hint: string;
}[] = [
  {
    value: 'Exact',
    label: 'Exact lane',
    hint: 'Direct run — collect at the origin, deliver at the destination, nothing in between.',
  },
  {
    value: 'Contains',
    label: 'Contains stops',
    hint: 'Same origin and destination, with any stops along the way.',
  },
];

/** Short grid label for a lane's match mode. */
export function laneMatchModeLabel(mode: LaneMatchModeValue): string {
  return mode === 'Contains' ? 'Contains stops' : 'Exact lane';
}

/** A row in the Transport "Route charges" sub-table (one per route line). */
export interface RouteOrAgreedRule {
  /** The LINE id — unique per row. */
  id: string;
  /**
   * The owning sheet's id. Edit and delete both act on the whole lane, not the
   * one line: a lane is one agreement, and its cargo types are edited together.
   */
  sheetId: string;
  /** How many cargo lines share this lane — so a delete can say what it takes. */
  sheetLineCount: number;
  lane: string;
  /** Direct run only, or the same endpoints with stops en route. */
  matchMode: LaneMatchModeValue;
  /** Charged on its own when a job matches, or offered first. */
  autoApply: boolean;
  appliesTo: string[];
  charge: string;
  detail: string;
  fromLabel: string;
  toLabel: string;
  status: PeriodStatus;
}

// ── Fuel Surcharge ─────────────────────────────────────────────────────────

export interface FuelSurchargeRow {
  id: string;
  name: string;
  /** Display territory, e.g. "GB" or "EU". */
  territory: string;
  rate: string;
  fromLabel: string;
  toLabel: string;
  status: PeriodStatus;
}

/**
 * Territory options for the fuel-surcharge form — each carries the underlying
 * scope (country or region) the BE stores. Starter taxonomy; extend as the
 * footprint grows. IE/NI is one country (XI normalises to IE on the order side).
 */
export interface FuelTerritoryOption {
  label: string;
  kind: FuelScopeKindValue;
  code: string;
}

export const FUEL_TERRITORIES: readonly FuelTerritoryOption[] = [
  { label: 'IE / NI', kind: 'Country', code: 'IE' },
  { label: 'GB', kind: 'Country', code: 'GB' },
  { label: 'EU', kind: 'Region', code: 'EU' },
  { label: 'International', kind: 'Region', code: 'INTL' },
] as const;

/** Label shown for an All-scope (flat) surcharge — applies regardless of territory. */
export const FUEL_ALL_TERRITORIES_LABEL = 'All deliveries';

/** Display label for a stored scope (kind + code) — falls back to the code. */
export function fuelTerritoryLabel(kind: FuelScopeKindValue, code: string | null): string {
  if (kind === 'All') return FUEL_ALL_TERRITORIES_LABEL;
  return FUEL_TERRITORIES.find((t) => t.kind === kind && t.code === code)?.label ?? code ?? '—';
}

// ── Additionals (everything that isn't Transport or Fuel) ───────────────────

export interface AdditionalRow {
  id: string;
  charge: string;
  /** Sub-label under the charge name, e.g. the charge type. */
  sub: string;
  /** "Flat", "Per hour", "% of base". */
  rateType: string;
  /** Pre-formatted rate, e.g. "€25.00", "€35.00 / hr", "15%". */
  rate: string;
  /** Whether the fuel surcharge applies on top of this charge. */
  fsApplicable: boolean;
  /** Source charge-definition id — a customer can't have the same one twice. */
  chargeDefId?: string;
}
